import { HttpClient } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { SaphirDirectoryUser, SaphirUserDirectory } from '@saphir/module-contract';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';
import { ServiceEmployeeCompactList } from '../core/umc/service-api.model';
import { CompanyContextService } from './company-context.service';

/**
 * `SAPHIR_USER_DIRECTORY` — the UMC users of the active company, for modules
 * that show or pick the person behind a stored `umcUserId`.
 *
 * Backed by `GET /service/companies/{id}/employees/compact` — the roster
 * listing of UMC's `/service` surface (there is no `/users` listing there:
 * the two `/users/...` routes answer for ONE person). The compact view is
 * exactly directory-shaped: id, display name, e-mail, account status. The
 * roster also lists people with no account (`no-account`, e-mail null) —
 * kept since contract 0.3.4: every employee the payroll module creates starts
 * as such a roster person, and the module shows them by name.
 *
 * One roster request per company and TTL (`ROSTER_TTL_MS`), shared by every
 * concurrent `findById`/`search`: a list page resolving twenty linked ids
 * costs one round trip, not twenty — and never one per re-render.
 *
 * A failed call degrades gracefully:
 * search resolves to an empty list, `findById` to `null` — the same answers
 * an empty directory would give, so consuming modules render their
 * "unresolvable user" state and nothing breaks. One warning per session
 * marks the degradation in the console. A failed load is not cached, so the
 * next lookup asks UMC again.
 *
 * Mock mode serves a fixed development list per mock company, so the picker
 * dialogs and the three display states of a user-account panel can be
 * developed without a running UMC.
 */
@Service()
export class UserDirectoryService implements SaphirUserDirectory {
  private readonly http = inject(HttpClient);
  private readonly companyContext = inject(CompanyContextService);

  private warned = false;

  /** The roster of one company, in flight or settled, with its fetch time. */
  private cached: RosterCache | null = null;

  async search(query: string): Promise<readonly SaphirDirectoryUser[]> {
    const users = await this.usersOfActiveCompany();
    const needle = query.trim().toLowerCase();

    if (!needle) {
      return users.slice(0, SEARCH_CAP);
    }

    return users
      .filter(
        (user) =>
          user.displayName.toLowerCase().includes(needle) ||
          user.email.toLowerCase().includes(needle),
      )
      .slice(0, SEARCH_CAP);
  }

  async findById(umcUserId: number): Promise<SaphirDirectoryUser | null> {
    const users = await this.usersOfActiveCompany();

    return users.find((user) => user.umcUserId === umcUserId) ?? null;
  }

  private usersOfActiveCompany(): Promise<readonly SaphirDirectoryUser[]> {
    const companyId = this.companyContext.activeCompanyId();

    if (companyId === null) {
      return Promise.resolve([]);
    }

    if (environment.auth.useMockAuth) {
      return Promise.resolve(MOCK_DIRECTORY.get(companyId) ?? []);
    }

    const now = Date.now();

    if (this.cached?.companyId === companyId && now - this.cached.fetchedAt < ROSTER_TTL_MS) {
      return this.cached.users;
    }

    const users = this.loadRoster(companyId).catch((error: unknown) => {
      // Not kept: the next lookup asks again instead of serving a failure for a minute.
      this.cached = null;

      if (!this.warned) {
        this.warned = true;
        console.warn(
          '[umc] company roster unavailable — user names will not resolve.',
          error,
        );
      }

      return [] as readonly SaphirDirectoryUser[];
    });

    this.cached = { companyId, fetchedAt: now, users };

    return users;
  }

  private async loadRoster(companyId: number): Promise<readonly SaphirDirectoryUser[]> {
    const roster = await firstValueFrom(
      this.http.get<ServiceEmployeeCompactList>(
        `${environment.umc.baseUrl}/service/companies/${companyId}/employees/compact`,
      ),
    );

    return roster.employees.flatMap((employee) => {
      const status = toDirectoryStatus(employee.status);

      return status === null
        ? []
        : [
            {
              umcUserId: employee.userId,
              displayName: employee.name,
              email: employee.email ?? '',
              status,
            },
          ];
    });
  }
}

interface RosterCache {
  readonly companyId: number;
  readonly fetchedAt: number;
  readonly users: Promise<readonly SaphirDirectoryUser[]>;
}

/** Enough to fill a picker dialog; the dialog searches to narrow further. */
const SEARCH_CAP = 50;

/** How long a company roster is reused before UMC is asked again. */
const ROSTER_TTL_MS = 60_000;

/**
 * Narrows a roster status onto the contract's union — `null` for anything
 * else (whatever UMC adds tomorrow): an unknown state must drop the row, not
 * masquerade as a known one.
 */
function toDirectoryStatus(status: string): SaphirDirectoryUser['status'] | null {
  return status === 'active' || status === 'pending' || status === 'disabled' || status === 'no-account'
    ? status
    : null;
}

/**
 * Development users per mock company (see `MOCK_CONTEXT` in
 * `CompanyContextService`). Ids deliberately do not start at 1, so a fixture
 * referencing an unknown id stays possible — that is the third display state
 * of a user-account panel.
 */
const MOCK_DIRECTORY: ReadonlyMap<number, readonly SaphirDirectoryUser[]> = new Map([
  [
    1,
    [
      {
        umcUserId: 10,
        displayName: 'Anna Adler',
        email: 'anna.adler@saphir.example',
        status: 'active',
      },
      {
        umcUserId: 11,
        displayName: 'Bernd Brandt',
        email: 'bernd.brandt@saphir.example',
        status: 'active',
      },
      {
        umcUserId: 12,
        displayName: 'Clara Curt',
        email: 'clara.curt@saphir.example',
        status: 'pending',
      },
      {
        umcUserId: 13,
        displayName: 'Dario Dietz',
        email: 'dario.dietz@saphir.example',
        status: 'disabled',
      },
    ],
  ],
  [
    2,
    [
      {
        umcUserId: 20,
        displayName: 'Erik Ernst',
        email: 'erik.ernst@muster.example',
        status: 'active',
      },
      {
        umcUserId: 21,
        displayName: 'Frida Falk',
        email: 'frida.falk@muster.example',
        status: 'active',
      },
    ],
  ],
]);
